import React, { useEffect, useState, useCallback } from "react";
import { ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import { formatDate, formatDateOnly } from "@/lib/date";
import { useAuth } from "@/context/AuthContext";
import DocStatusBadge from "@/components/documentdepot/DocStatusBadge";

export default function DocumentReview() {
  const { viewOnly } = useAuth();
  const [items, setItems] = useState([]);
  const [confirm, setConfirm] = useState(null); // {row, action}
  const [returnFor, setReturnFor] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/document-depot/review"); setItems(data.items || []); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doAction = async () => {
    if (!confirm) return;
    try {
      await api.post(`/document-depot/submissions/${confirm.row.submission_id}/${confirm.action}`,
        confirm.action === "approve" ? undefined : { note: "" });
      toast.success("Document approved");
      setConfirm(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doReturn = async () => {
    if (!note.trim()) { toast.error("A note is required to return a document."); return; }
    try {
      await api.post(`/document-depot/submissions/${returnFor.submission_id}/return`, { note });
      toast.success("Returned for changes");
      setReturnFor(null); setNote(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="document-review-page">
      <PageHeader title="Document Review" subtitle="Review pending document uploads across all requests for your team" />

      {items.length === 0 ? (
        <EmptyState title="Nothing to review" description="Uploaded documents awaiting your review will appear here." testId="doc-review-empty" />
      ) : (
        <Card className="border-slate-200 shadow-sm overflow-x-auto">
          <Table data-testid="doc-review-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                {["Form", "Member", "File", "Folder", "Turned in", "Status", "Actions"].map((h) => (
                  <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.submission_id} data-testid={`doc-review-row-${r.submission_id}`}>
                  <TableCell className="text-sm font-medium">{r.form_name}</TableCell>
                  <TableCell className="text-sm">{r.member_name}</TableCell>
                  <TableCell className="text-sm">
                    {r.web_view_link ? (
                      <a href={r.web_view_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-sky-600 hover:underline" data-testid={`doc-review-file-${r.submission_id}`}>
                        {r.file_name || "Open"} <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : <span className="text-slate-400">{r.file_name || "—"}</span>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{r.category_name || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.turned_in_date ? formatDateOnly(r.turned_in_date) : formatDate(r.submitted_at)}</TableCell>
                  <TableCell><DocStatusBadge status={r.status} testId={`doc-review-status-${r.submission_id}`} /></TableCell>
                  <TableCell>
                    {!viewOnly ? (
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setConfirm({ row: r, action: "approve" })} data-testid={`approve-doc-${r.submission_id}`}>Approve</Button>
                        <Button size="sm" variant="outline" className="rounded-full text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => { setReturnFor(r); setNote(""); }} data-testid={`return-doc-${r.submission_id}`}>Return</Button>
                      </div>
                    ) : <span className="text-xs text-slate-400">View only</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}
        title="Approve document?"
        description={confirm ? `${confirm.row.member_name}'s upload for "${confirm.row.form_name}" will be approved.` : ""}
        confirmText="Approve" onConfirm={doAction} testId="doc-review-confirm-dialog"
      />

      <Dialog open={!!returnFor} onOpenChange={(o) => !o && setReturnFor(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="doc-return-dialog">
          <DialogHeader><DialogTitle className="font-heading">Return for changes</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Reason / note (required)</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="doc-return-note-input" placeholder="Explain what needs fixing…" />
          </div>
          <DialogFooter>
            <Button onClick={doReturn} className="rounded-full bg-amber-600 hover:bg-amber-700" data-testid="doc-return-submit-btn">Return document</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
